import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  InventoryItemRecord,
  InventoryStockLocationRecord,
  InventoryStockMovementFilterParams,
  InventoryStockMovementItemOptionFilterParams,
  InventoryStockMovementItemOptionRecord,
  InventoryStockMovementItemSummaryRow,
  InventoryStockMovementRecord,
  InventoryStockMovementSummaryCounts,
} from '../types/inventory';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;
const EXPORT_PAGE_SIZE = 1000;

const INCOMING_MOVEMENT_TYPES = new Set<
  InventoryStockMovementRecord['movement_type']
>(['purchase_receipt', 'transfer_in', 'adjustment_increase']);

const OUTGOING_MOVEMENT_TYPES = new Set<
  InventoryStockMovementRecord['movement_type']
>([
  'sale_consumption',
  'transfer_out',
  'adjustment_decrease',
  'wastage',
  'return_to_supplier',
]);

const toNumber = (value: unknown) => {
  if (typeof value === 'number') {
    return value;
  }

  return Number(value || 0);
};

const toMovementTimestampStart = (value: string) => `${value}T00:00:00`;
const toMovementTimestampEnd = (value: string) => `${value}T23:59:59.999`;

const mapInventoryStockMovement = (
  value: Record<string, unknown>,
): InventoryStockMovementRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  outlet_id: String(value.outlet_id || ''),
  stock_location_id: String(value.stock_location_id || ''),
  inventory_item_id: String(value.inventory_item_id || ''),
  movement_type:
    value.movement_type as InventoryStockMovementRecord['movement_type'],
  reference_type: (value.reference_type as string | null) ?? null,
  reference_id: (value.reference_id as string | null) ?? null,
  order_bill_number:
    value.order_bill_number === null || value.order_bill_number === undefined
      ? null
      : toNumber(value.order_bill_number),
  quantity: toNumber(value.quantity),
  unit_cost: value.unit_cost === null ? null : toNumber(value.unit_cost),
  total_cost: value.total_cost === null ? null : toNumber(value.total_cost),
  movement_date: String(value.movement_date || ''),
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
});

const applyStockMovementBaseFilters = <T>(
  query: T,
  filters: {
    stockLocationId?: InventoryStockMovementFilterParams['stockLocationId'];
    movementType?: InventoryStockMovementFilterParams['movementType'];
    inventoryItemIds?: InventoryStockMovementFilterParams['inventoryItemIds'];
    dateFrom?: InventoryStockMovementFilterParams['dateFrom'];
    dateTo?: InventoryStockMovementFilterParams['dateTo'];
  },
) => {
  let nextQuery = query as any;

  if (filters.stockLocationId && filters.stockLocationId !== 'all') {
    nextQuery = nextQuery.eq('stock_location_id', filters.stockLocationId);
  }

  if (filters.movementType && filters.movementType !== 'all') {
    nextQuery = nextQuery.eq('movement_type', filters.movementType);
  }

  if (filters.inventoryItemIds?.length) {
    nextQuery = nextQuery.in('inventory_item_id', filters.inventoryItemIds);
  }

  if (filters.dateFrom) {
    nextQuery = nextQuery.gte(
      'movement_date',
      toMovementTimestampStart(filters.dateFrom),
    );
  }

  if (filters.dateTo) {
    nextQuery = nextQuery.lte(
      'movement_date',
      toMovementTimestampEnd(filters.dateTo),
    );
  }

  return nextQuery as T;
};

const buildMovementSearchLookup = (
  items: InventoryItemRecord[],
  locations: InventoryStockLocationRecord[],
  search?: string,
) => {
  const trimmedSearch = search?.trim().toLowerCase();
  if (!trimmedSearch) {
    return null;
  }

  const matchingItemIds = new Set(
    items
      .filter((item) =>
        [item.name, item.sku ?? '', item.description ?? '']
          .join(' ')
          .toLowerCase()
          .includes(trimmedSearch),
      )
      .map((item) => item.id),
  );

  const matchingLocationIds = new Set(
    locations
      .filter((location) =>
        [location.name, location.type, location.description ?? '']
          .join(' ')
          .toLowerCase()
          .includes(trimmedSearch),
      )
      .map((location) => location.id),
  );

  return { matchingItemIds, matchingLocationIds, trimmedSearch };
};

const fetchMovementSearchLookup = async (
  companyId: number,
  outletId: string,
  search?: string,
) => {
  if (!search?.trim()) {
    return {
      success: true as const,
      data: null,
    };
  }

  const [itemsResult, locationsResult] = await Promise.all([
    supabase
      .from('inventory_items')
      .select('*')
      .eq('company_id', companyId)
      .order('name', { ascending: true }),
    supabase
      .from('inventory_stock_locations')
      .select('*')
      .eq('company_id', companyId)
      .eq('outlet_id', outletId)
      .order('name', { ascending: true }),
  ]);

  if (itemsResult.error || locationsResult.error) {
    return {
      success: false as const,
      error: itemsResult.error?.message || locationsResult.error?.message,
      data: null,
    };
  }

  return {
    success: true as const,
    data: buildMovementSearchLookup(
      (itemsResult.data || []) as InventoryItemRecord[],
      (locationsResult.data || []) as InventoryStockLocationRecord[],
      search,
    ),
  };
};

const applyStockMovementSearchFilter = <T>(
  query: T,
  searchLookup: ReturnType<typeof buildMovementSearchLookup>,
) => {
  if (!searchLookup) {
    return query;
  }

  let nextQuery = query as any;
  const itemIds = Array.from(searchLookup.matchingItemIds);
  const locationIds = Array.from(searchLookup.matchingLocationIds);
  const searchTerm = searchLookup.trimmedSearch;

  const ors: string[] = [];
  if (itemIds.length) {
    ors.push(`inventory_item_id.in.(${itemIds.join(',')})`);
  }
  if (locationIds.length) {
    ors.push(`stock_location_id.in.(${locationIds.join(',')})`);
  }
  if (searchTerm) {
    ors.push(
      `movement_type::text.ilike.%${searchTerm}%,reference_type.ilike.%${searchTerm}%,reference_id.ilike.%${searchTerm}%`,
    );
  }

  if (ors.length) {
    nextQuery = nextQuery.or(ors.join(','));
  }

  return nextQuery as T;
};

const fetchMovedInventoryItemIds = async ({
  companyId,
  outletId,
  stockLocationId = 'all',
  movementType = 'all',
  dateFrom,
  dateTo,
}: Omit<
  InventoryStockMovementItemOptionFilterParams,
  'page' | 'pageSize' | 'search'
>): Promise<ApiResponse<string[]>> => {
  const itemIds = new Set<string>();
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const from = (page - 1) * EXPORT_PAGE_SIZE;
    const to = from + EXPORT_PAGE_SIZE - 1;

    let movementQuery = supabase
      .from('inventory_stock_movements')
      .select('inventory_item_id')
      .eq('company_id', companyId)
      .eq('outlet_id', outletId)
      .range(from, to);

    movementQuery = applyStockMovementBaseFilters(movementQuery, {
      stockLocationId,
      movementType,
      dateFrom,
      dateTo,
    });

    // eslint-disable-next-line no-await-in-loop
    const movementResult = await movementQuery;

    if (movementResult.error) {
      return {
        success: false,
        error: movementResult.error.message,
        data: undefined,
      };
    }

    const rows = movementResult.data || [];
    rows.forEach((row) => {
      const itemId = String(row.inventory_item_id || '');
      if (itemId) {
        itemIds.add(itemId);
      }
    });

    hasMore = rows.length === EXPORT_PAGE_SIZE;
    page += 1;
  }

  return { success: true, data: Array.from(itemIds) };
};

export const fetchInventoryStockMovements = async ({
  companyId,
  outletId,
  search,
  stockLocationId = 'all',
  movementType = 'all',
  inventoryItemIds = [],
  dateFrom,
  dateTo,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: InventoryStockMovementFilterParams): Promise<
  ApiResponse<PaginatedResponse<InventoryStockMovementRecord>>
> => {
  try {
    const searchLookupResult = await fetchMovementSearchLookup(
      companyId,
      outletId,
      search,
    );

    if (!searchLookupResult.success) {
      return {
        success: false,
        error: searchLookupResult.error,
        data: undefined,
      };
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('inventory_stock_movements_view')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId)
      .eq('outlet_id', outletId)
      .order('movement_date', { ascending: false })
      .order('created_at', { ascending: false })
      .range(from, to);

    query = applyStockMovementBaseFilters(query, {
      stockLocationId,
      movementType,
      inventoryItemIds,
      dateFrom,
      dateTo,
    });

    query = applyStockMovementSearchFilter(query, searchLookupResult.data);

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const mappedRows = (data || []).map((row) =>
      mapInventoryStockMovement(row as Record<string, unknown>),
    );

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: mappedRows,
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory stock movements',
      data: undefined,
    };
  }
};

export const fetchInventoryStockMovementItemOptions = async ({
  companyId,
  outletId,
  search,
  stockLocationId = 'all',
  movementType = 'all',
  dateFrom,
  dateTo,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: InventoryStockMovementItemOptionFilterParams): Promise<
  ApiResponse<PaginatedResponse<InventoryStockMovementItemOptionRecord>>
> => {
  try {
    const movedItemIdsResult = await fetchMovedInventoryItemIds({
      companyId,
      outletId,
      stockLocationId,
      movementType,
      dateFrom,
      dateTo,
    });

    if (!movedItemIdsResult.success || !movedItemIdsResult.data) {
      return {
        success: false,
        error:
          movedItemIdsResult.error ||
          'Failed to find items with stock movements',
        data: undefined,
      };
    }

    const movedItemIds = movedItemIdsResult.data;

    if (!movedItemIds.length) {
      return {
        success: true,
        data: {
          data: [],
          pagination: {
            page,
            pageSize,
            totalItems: 0,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        },
      };
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const trimmedSearch = search?.trim();

    let itemQuery = supabase
      .from('inventory_items')
      .select('id,name,sku', { count: 'exact' })
      .eq('company_id', companyId)
      .in('id', movedItemIds)
      .order('name', { ascending: true })
      .range(from, to);

    if (trimmedSearch) {
      itemQuery = itemQuery.or(
        `sku.ilike.%${trimmedSearch}%,name.ilike.%${trimmedSearch}%,description.ilike.%${trimmedSearch}%`,
      );
    }

    const { data, error, count } = await itemQuery;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data || []).map((item) => ({
          id: String(item.id || ''),
          name: String(item.name || ''),
          sku: (item.sku as string | null) ?? null,
        })),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch stock movement item options',
      data: undefined,
    };
  }
};

export const fetchInventoryStockMovementExportRows = async (
  params: Omit<InventoryStockMovementFilterParams, 'page' | 'pageSize'>,
): Promise<ApiResponse<InventoryStockMovementRecord[]>> => {
  try {
    const rows: InventoryStockMovementRecord[] = [];
    let page = 1;
    let hasNextPage = true;

    while (hasNextPage) {
      // eslint-disable-next-line no-await-in-loop
      const result = await fetchInventoryStockMovements({
        ...params,
        page,
        pageSize: EXPORT_PAGE_SIZE,
      });

      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch stock movement export rows',
          data: undefined,
        };
      }

      rows.push(...result.data.data);
      hasNextPage = result.data.pagination.hasNextPage;
      page += 1;

      if (!result.data.data.length) {
        break;
      }
    }

    return { success: true, data: rows };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch stock movement export rows',
      data: undefined,
    };
  }
};

const getQuantityIn = (movement: InventoryStockMovementRecord): number => {
  if (INCOMING_MOVEMENT_TYPES.has(movement.movement_type)) {
    return Math.abs(movement.quantity);
  }

  if (
    movement.movement_type === 'stock_count_correction' &&
    movement.quantity > 0
  ) {
    return Math.abs(movement.quantity);
  }

  return 0;
};

const getQuantityOut = (movement: InventoryStockMovementRecord): number => {
  if (OUTGOING_MOVEMENT_TYPES.has(movement.movement_type)) {
    return Math.abs(movement.quantity);
  }

  if (
    movement.movement_type === 'stock_count_correction' &&
    movement.quantity < 0
  ) {
    return Math.abs(movement.quantity);
  }

  return 0;
};

export const fetchInventoryStockMovementSummary = async (
  companyId: number,
  outletId: string,
  search?: string,
  stockLocationId?: InventoryStockMovementFilterParams['stockLocationId'],
  movementType?: InventoryStockMovementFilterParams['movementType'],
  inventoryItemIds?: InventoryStockMovementFilterParams['inventoryItemIds'],
  dateFrom?: string,
  dateTo?: string,
): Promise<ApiResponse<InventoryStockMovementSummaryCounts>> => {
  try {
    const normalizedStockLocationId = stockLocationId ?? 'all';
    const normalizedMovementType = movementType ?? 'all';
    const normalizedInventoryItemIds = inventoryItemIds ?? [];

    const movementsResult = await fetchInventoryStockMovementExportRows({
      companyId,
      outletId,
      search,
      stockLocationId: normalizedStockLocationId,
      movementType: normalizedMovementType,
      inventoryItemIds: normalizedInventoryItemIds,
      dateFrom,
      dateTo,
    });

    if (!movementsResult.success) {
      return {
        success: false,
        error: movementsResult.error,
        data: undefined,
      };
    }

    const movements = movementsResult.data ?? [];
    const totalQuantityIn = movements.reduce(
      (total, movement) => total + getQuantityIn(movement),
      0,
    );
    const totalQuantityOut = movements.reduce(
      (total, movement) => total + getQuantityOut(movement),
      0,
    );
    const totalMovementValue = movements.reduce(
      (total, movement) => total + Math.abs(movement.total_cost ?? 0),
      0,
    );

    return {
      success: true,
      data: {
        totalMovements: movements.length,
        totalQuantityIn,
        totalQuantityOut,
        totalMovementValue,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory stock movement summary',
      data: undefined,
    };
  }
};

export const fetchInventoryStockMovementItemSummary = async (
  params: Omit<InventoryStockMovementFilterParams, 'page' | 'pageSize'>,
): Promise<ApiResponse<InventoryStockMovementItemSummaryRow[]>> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'get-inventory-stock-movement-summary',
      { body: params },
    );

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return {
      success: true,
      data: (data?.data ?? []) as InventoryStockMovementItemSummaryRow[],
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory stock movement item summary',
      data: undefined,
    };
  }
};
